import type { ViewContainerRef } from '@angular/core';
import { Injectable, inject } from '@angular/core';

import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { deepEqual } from 'fast-equals';
import type { Observable } from 'rxjs';
import { combineLatest, filter, map, switchMap, take, distinctUntilChanged, of, merge } from 'rxjs';

import { filterBoolean } from '@core/common-legacy';
import { LanguageConfigActions, LanguageConfigSelectors } from '@core/languages-data';
import { LanguagesService } from '@core/languages-legacy';
import type { LanguageItem } from '@core/languages-types';
import { LocationActions, LocationSelectors } from '@core/locations-data';
import { DialogService, DialogWidth } from '@core/user-interface-legacy';

import { ManageLocationDialogComponent } from './manage-location-dialog.component';
import type { ManageLocationCtrlData } from './manage-location-dialog.types';
import { LocationDialogContext } from './manage-location-dialog.types';

type LocationLanguageData = {
  defaultLanguage: string;
  availableLanguages: Array<LanguageItem & { isDefault: boolean }>;
  activeLanguage: string;
};

@Injectable({
  providedIn: 'root',
})
export class ManageLocationDialogControllerService {
  private readonly dialogService = inject(DialogService);
  private readonly store = inject(Store);
  private readonly languageService = inject(LanguagesService);
  private readonly actions$ = inject(Actions);

  public openNewLocationDialog$(
    data: ManageLocationCtrlData,
    vcRef: ViewContainerRef,
  ): Observable<boolean> {
    const { jobLanguages, jobPrimaryLanguageCode } = data;
    return this.setLanguagesData$({ jobLanguages, jobPrimaryLanguageCode }).pipe(
      take(1),
      switchMap(
        langData =>
          this.dialogService.openCustom(
            ManageLocationDialogComponent,
            { width: DialogWidth.Large },
            {
              ...langData,
              context: LocationDialogContext.Create,
              fromJob: !!jobLanguages,
              goBackHandler: data?.goBackHandler,
              saveHandler: data?.saveHandler,
            },
            vcRef,
          ).afterClosed,
      ),
    );
  }

  public openEditLocationDialog$(
    data: ManageLocationCtrlData,
    vcRef: ViewContainerRef,
  ): Observable<boolean> {
    const { id, langCode, jobLanguages, jobPrimaryLanguageCode } = data;
    this.store.dispatch(LocationActions.fetchOne({ payload: { id: id! } }));
    const location$ = this.store.select(LocationSelectors.byId(id!)).pipe(
      filterBoolean,
      filter(location => !!location?.translations),
    );
    const deleteLocationFromWS$ = this.actions$.pipe(
      ofType(LocationActions.deleteFromWS),
      filter(action => action.payload.ids.includes(id!)),
      switchMap(() => of(true)),
    );
    const archiveLocationFromWS$ = this.actions$.pipe(
      ofType(LocationActions.updateFromWS),
      filter(action => action.payload.ids.includes(id!)),
      switchMap(() => this.actions$.pipe(ofType(LocationActions.fetchOneSuccess))),
      filter(action => action.payload.data.id === id!),
      switchMap(() => location$),
      filter(location => !!location.archivedAt),
      switchMap(() => of(true)),
    );
    const close$ = merge(deleteLocationFromWS$, archiveLocationFromWS$);
    const updateLocation$ = this.actions$.pipe(
      ofType(LocationActions.updateFromWS),
      filter(action => action.payload.ids.includes(id!)),
      switchMap(() => this.actions$.pipe(ofType(LocationActions.fetchOneSuccess))),
      filter(action => action.payload.data.id === id!),
      switchMap(() => location$),
      filter(location => !location.archivedAt),
      distinctUntilChanged(deepEqual),
    );

    return combineLatest([
      this.setLanguagesData$({ langCode, jobLanguages, jobPrimaryLanguageCode }),
      location$,
    ]).pipe(
      take(1),
      switchMap(
        ([langData, location]) =>
          this.dialogService.openCustom(
            ManageLocationDialogComponent,
            { width: DialogWidth.Large },
            {
              ...langData,
              location,
              context: LocationDialogContext.Edit,
              id,
              updateLocation$,
              close$,
              fromJob: !!jobLanguages,
              goBackHandler: data?.goBackHandler,
              saveHandler: data?.saveHandler,
            },
            vcRef,
          ).afterClosed,
      ),
    );
  }

  private setLanguagesData$({
    langCode,
    jobLanguages,
    jobPrimaryLanguageCode,
  }: ManageLocationCtrlData): Observable<LocationLanguageData> {
    this.store.dispatch(LanguageConfigActions.fetch());

    return this.store.select(LanguageConfigSelectors.data).pipe(
      filterBoolean,
      map(data => {
        const languages = data.settings.multilangEnabled
          ? data.languages
          : [data.languages.find(lang => lang.id === data.settings.selectedLanguageId)!];
        return languages
          .filter(
            lang =>
              lang.id === data.settings.selectedLanguageId ||
              (lang.enabled && (jobLanguages ? jobLanguages.includes(lang.code) : true)),
          )
          .map(lang => ({
            ...lang,
            isDefault: lang.id === data.settings.selectedLanguageId,
            primary: lang.code === jobPrimaryLanguageCode,
            translationFieldsCount: 0,
          }))
          .sort((a, b) => (a.isDefault === b.isDefault ? 0 : a.isDefault ? -1 : 1));
      }),
      map((availableLanguages: Array<LanguageItem & { isDefault: boolean }>) => ({
        defaultLanguage: availableLanguages[0].code,
        availableLanguages,
        activeLanguage:
          langCode && this.languageService.isMultilangEnabled
            ? langCode
            : availableLanguages[0].code,
      })),
    );
  }
}
